import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, RefreshCcw, XCircle } from 'lucide-react';
import { useState } from 'react';

import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { EmptyState } from '@/components/common/empty-state';
import { PageHeader } from '@/components/common/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { extractApiErrorMessage } from '@/lib/api';
import {
  listAdminInvitations,
  resendAdminInvitation,
  revokeAdminInvitation,
  type AdminInvitationRow,
} from '@/lib/super-admin-api';

const PAGE_SIZE = 20;

export function SuperAdminInvitationsPage() {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [status, setStatus] = useState<'ALL' | 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED'>(
    'PENDING',
  );
  const [page, setPage] = useState(0);
  const [confirmRevoke, setConfirmRevoke] = useState<AdminInvitationRow | null>(null);

  const query = useQuery({
    queryKey: ['super-admin', 'invitations', { status, page }],
    queryFn: () =>
      listAdminInvitations({
        ...(status !== 'ALL' ? { status } : {}),
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
  });

  const resend = useMutation({
    mutationFn: (id: string) => resendAdminInvitation(id),
    onSuccess: () => {
      success('Invitation resent', 'A fresh link is on its way');
      qc.invalidateQueries({ queryKey: ['super-admin', 'invitations'] });
    },
    onError: (e) => error('Resend failed', extractApiErrorMessage(e)),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeAdminInvitation(id),
    onSuccess: () => {
      success('Invitation revoked');
      qc.invalidateQueries({ queryKey: ['super-admin', 'invitations'] });
      qc.invalidateQueries({ queryKey: ['super-admin', 'dashboard'] });
      setConfirmRevoke(null);
    },
    onError: (e) => error('Revoke failed', extractApiErrorMessage(e)),
  });

  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const empty = !query.isLoading && (query.data?.items.length ?? 0) === 0;

  return (
    <div className="mx-auto w-full max-w-6xl p-6">
      <PageHeader
        title="Org admin invitations"
        description="Every pending or historical invitation for organization admins."
      />

      <Card className="mb-4">
        <CardContent className="flex items-end gap-3 p-4">
          <div className="w-48">
            <Label
              htmlFor="inv-status"
              className="mb-1 block text-xs uppercase text-muted-foreground"
            >
              Status
            </Label>
            <Select
              id="inv-status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as typeof status);
                setPage(0);
              }}
            >
              <option value="ALL">All</option>
              <option value="PENDING">Pending</option>
              <option value="ACCEPTED">Accepted</option>
              <option value="EXPIRED">Expired</option>
              <option value="REVOKED">Revoked</option>
            </Select>
          </div>
        </CardContent>
      </Card>

      {query.isError ? (
        <EmptyState
          icon={Mail}
          title="Could not load invitations"
          description={extractApiErrorMessage(query.error)}
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : empty ? (
        <EmptyState
          icon={Mail}
          title="No invitations found"
          description="Create an organization from the Organizations page to issue the first admin invitation."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Attempts</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.isLoading
                  ? Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={`sk-${i}`}>
                        {Array.from({ length: 7 }).map((__, j) => (
                          <TableCell key={`sk-${i}-${j}`}>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  : query.data?.items.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell className="font-mono text-xs">{inv.email}</TableCell>
                        <TableCell>
                          <div className="font-medium">{inv.organization.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {inv.organization.slug}
                          </div>
                        </TableCell>
                        <TableCell>{inv.roleName}</TableCell>
                        <TableCell>
                          <StatusBadge status={inv.status} />
                        </TableCell>
                        <TableCell className="tabular-nums">{inv.attemptCount}</TableCell>
                        <TableCell className="tabular-nums text-xs text-muted-foreground">
                          {new Date(inv.expiresAt).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={inv.status === 'ACCEPTED'}
                              onClick={() => resend.mutate(inv.id)}
                            >
                              <RefreshCcw className="mr-1 h-3 w-3" aria-hidden /> Resend
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              disabled={inv.status !== 'PENDING'}
                              onClick={() => setConfirmRevoke(inv)}
                            >
                              <XCircle className="mr-1 h-3 w-3" aria-hidden /> Revoke
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {total > PAGE_SIZE ? (
        <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Page {page + 1} of {totalPages} · {total} total
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(confirmRevoke)}
        title={`Revoke invitation for ${confirmRevoke?.email ?? ''}?`}
        description="The current link will stop working immediately. You can always resend a new one later."
        confirmLabel="Revoke"
        destructive
        onCancel={() => setConfirmRevoke(null)}
        onConfirm={() => {
          if (!confirmRevoke) return Promise.resolve();
          return revoke.mutateAsync(confirmRevoke.id);
        }}
      />
    </div>
  );
}

function StatusBadge({ status }: { status: AdminInvitationRow['status'] }) {
  switch (status) {
    case 'PENDING':
      return <Badge variant="warning">Pending</Badge>;
    case 'ACCEPTED':
      return <Badge variant="success">Accepted</Badge>;
    case 'EXPIRED':
      return <Badge variant="outline">Expired</Badge>;
    case 'REVOKED':
      return <Badge variant="destructive">Revoked</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}
